import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import { Plus } from 'lucide-react';

const KINDS = ['feature', 'api_change', 'schema_change', 'deployment', 'decision', 'changelog', 'note'];

export default function KnowledgeBase({ isProd }) {
  const [entries, setEntries] = useState([]);
  const [filter, setFilter] = useState('');
  const [kindFilter, setKindFilter] = useState('');
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ kind: 'note', title: '', body: '', tags: '' });

  const load = (kind = kindFilter, search = filter) => {
    const params = new URLSearchParams();
    if (kind) params.set('kind', kind);
    if (search) params.set('search', search);
    api.get(`/lab/knowledge?${params}`)
      .then(({ data }) => setEntries(data.entries || []))
      .catch(() => {});
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/lab/knowledge', {
        kind: form.kind,
        title: form.title,
        body: form.body,
        tags: form.tags ? form.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
      });
      toast.success('Memory entry added');
      setForm({ kind: 'note', title: '', body: '', tags: '' });
      setAdding(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Add failed');
    }
  };

  return (
    <div data-testid="lab-knowledge" className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <select
          data-testid="lab-knowledge-kind-filter"
          value={kindFilter}
          onChange={(e) => { setKindFilter(e.target.value); load(e.target.value, filter); }}
          className="bg-transparent border border-[var(--border-default)] px-2 py-1 text-[10px] font-mono uppercase tracking-wider"
        >
          <option value="">all kinds</option>
          {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
        <input
          data-testid="lab-knowledge-search"
          type="text"
          placeholder="search title/body…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') load(kindFilter, filter); }}
          className="bg-transparent border border-[var(--border-default)] px-2 py-1 text-[11px] font-mono flex-1 min-w-[180px]"
        />
        <button
          data-testid="lab-knowledge-add-toggle"
          onClick={() => setAdding((v) => !v)}
          disabled={!isProd}
          title={isProd ? 'Add memory entry' : 'Production only'}
          className="flex items-center gap-1 border border-cyan-sys text-cyan-sys px-2 py-1 text-[10px] uppercase tracking-[0.2em] hover:bg-cyan-sys hover:text-black disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <Plus size={11} /> add
        </button>
      </div>

      {adding && isProd && (
        <form onSubmit={submit} data-testid="lab-knowledge-form" className="border border-cyan-sys p-3 space-y-2 text-[11px]">
          <div className="flex gap-2">
            <select
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value })}
              className="bg-transparent border border-[var(--border-default)] px-2 py-1 text-[11px] uppercase"
            >
              {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            <input
              type="text" placeholder="title" required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="bg-transparent border border-[var(--border-default)] px-2 py-1 flex-1"
            />
          </div>
          <textarea
            placeholder="body (markdown ok)"
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            className="bg-transparent border border-[var(--border-default)] px-2 py-1 w-full h-32 font-mono"
          />
          <input
            type="text" placeholder="tags (comma separated)"
            value={form.tags}
            onChange={(e) => setForm({ ...form, tags: e.target.value })}
            className="bg-transparent border border-[var(--border-default)] px-2 py-1 w-full"
          />
          <button type="submit" className="border border-buy text-buy px-3 py-1 uppercase tracking-[0.2em] hover:bg-buy hover:text-black">
            save
          </button>
        </form>
      )}

      <div className="space-y-2">
        {entries.length === 0 && (
          <div className="text-[11px] text-[var(--txt-secondary)] p-4 border border-[var(--border-default)]">
            No entries match this filter.
          </div>
        )}
        {entries.map((e) => (
          <div key={e.id} data-testid={`lab-memory-${e.id}`} className="border border-[var(--border-default)] p-3">
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="text-[12px] text-[var(--txt-primary)]">{e.title}</div>
              <div className="text-[9px] tracking-[0.25em] uppercase text-[var(--txt-secondary)]">
                {e.kind} · {(e.timestamp || '').slice(0, 10)}
              </div>
            </div>
            {e.body && (
              <div className="markdown-body text-[11px] text-[var(--txt-secondary)] leading-relaxed">
                <ReactMarkdown>{e.body}</ReactMarkdown>
              </div>
            )}
            {(e.files?.length || e.tags?.length) ? (
              <div className="mt-1 flex flex-wrap gap-1 text-[9px] font-mono">
                {(e.files || []).map((f) => (
                  <span key={f} className="text-cyan-sys">{f}</span>
                ))}
                {(e.tags || []).map((t) => (
                  <span key={t} className="border border-[var(--border-default)] px-1 text-[var(--txt-muted)]">#{t}</span>
                ))}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
